import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Calendar, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_BLOGS = [
  { id: '1', title_en: 'Importance of HPV Vaccination for Women', title_ta: 'பெண்களுக்கு HPV தடுப்பூசியின் முக்கியத்துவம்', excerpt_en: 'Learn about HPV vaccination and why it is crucial for women\'s health and cancer prevention.', excerpt_ta: 'HPV தடுப்பூசி பற்றி அறியுங்கள்.', image_url: 'https://images.unsplash.com/photo-1577563612200-a95560e2edec?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800', category: "Women's Health", author: 'Dr. Sivakamu', created_at: new Date().toISOString() },
  { id: '2', title_en: 'Regular Health Screenings for Women', title_ta: 'பெண்களுக்கான வழக்கமான பரிசோதனைகள்', excerpt_en: 'Early detection through regular screenings saves lives.', excerpt_ta: 'ஆரம்ப கண்டறிதல் உயிர்களைக் காப்பாற்றுகிறது.', image_url: 'https://images.unsplash.com/photo-1609113160023-4e31f3765fd7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800', category: 'Diagnostics', author: 'Medical Team', created_at: new Date().toISOString() },
  { id: '3', title_en: '#LetsTalkPeriods - Menstrual Health Awareness', title_ta: '#LetsTalkPeriods - மாதவிடாய் ஆரோக்கியம்', excerpt_en: 'Breaking taboos about menstrual health for women of all ages.', excerpt_ta: 'மாதவிடாய் ஆரோக்கியம் பற்றிய விழிப்புணர்வு.', image_url: 'https://images.unsplash.com/photo-1559757175-053139280de2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800', category: "Awareness", author: 'Care Team', created_at: new Date().toISOString() },
];

const BlogSection = () => {
  const { t, language } = useLanguage();
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await axios.get(`${API}/blogs`);
        setBlogs(res.data?.length > 0 ? res.data : FALLBACK_BLOGS);
      } catch {
        setBlogs(FALLBACK_BLOGS);
      }
    };
    fetchBlogs();
  }, []);

  const getTitle = (b) => language === 'ta' ? b.title_ta : b.title_en;
  const getExcerpt = (b) => language === 'ta' ? b.excerpt_ta : b.excerpt_en;

  return (
    <section id="blog" className="py-14 bg-white" data-testid="blog-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 bg-[#0066b3]/8 text-[#0066b3] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
            {language === 'ta' ? 'ஆரோக்கிய குறிப்புகள்' : 'Health Tips'}
          </span>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0f172a] tracking-tight">
            {t('blog.title')}
          </h2>
        </div>

        {/* Horizontal compact blog cards */}
        <div className="space-y-4">
          {blogs.slice(0, 5).map((blog) => (
            <article
              key={blog.id}
              className="group flex flex-col sm:flex-row gap-4 bg-[#f8fafc] rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all"
              data-testid={`blog-card-${blog.id}`}
            >
              <div className="sm:w-40 h-28 sm:h-auto rounded-lg overflow-hidden flex-shrink-0">
                <img src={blog.image_url} alt={getTitle(blog)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="flex-1 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs text-[#94a3b8] mb-1.5">
                  <span className="px-2 py-0.5 bg-[#0066b3]/10 text-[#0066b3] rounded-full font-medium">{blog.category}</span>
                  <span className="flex items-center gap-1"><User size={10} />{blog.author}</span>
                </div>
                <h3 className="text-sm font-bold text-[#0f172a] mb-1 group-hover:text-[#0066b3] transition-colors line-clamp-1">{getTitle(blog)}</h3>
                <p className="text-xs text-[#64748b] line-clamp-2 mb-2">{getExcerpt(blog)}</p>
                <button className="inline-flex items-center gap-1 text-xs font-semibold text-[#0066b3]">
                  {t('blog.readMore')} <ArrowRight size={12} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
